import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRealtimeAccess } from './useRealtimeAccess';

interface PaymentStatus {
  hasAccess: boolean;
  hasPaid: boolean;
  planName: string | null;
  amount: number | null;
  daysRemaining: number | null;
  expiresAt: string | null;
  loading: boolean;
  markPaymentCompleted: () => void;
}

interface VerifyUserAccessResult {
  has_access: boolean;
  plan_name: string | null;
  amount: number | null;
  source: string;
  expires_at: string | null;
  days_remaining: number | null;
}

export const usePaymentStatus = (): PaymentStatus => {
  // Initialize from localStorage to prevent flicker
  const [hasAccess, setHasAccess] = useState(() => {
    const cached = localStorage.getItem('payment_plan');
    return !!cached;
  });
  const [loading, setLoading] = useState(true);
  const [planName, setPlanName] = useState<string | null>(() => {
    return localStorage.getItem('payment_plan') || null;
  });
  const [amount, setAmount] = useState<number | null>(() => {
    const cached = localStorage.getItem('payment_amount');
    return cached ? parseFloat(cached) : null;
  });
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | undefined>();

  const checkPaymentStatus = useCallback(async () => {
    try {
      // SECURITY FIX: Only check localStorage for UI optimization, never for authorization
      const storedSessionId = localStorage.getItem('payment_session_id');

      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();
      setUserEmail(user?.email);
      
      if (!user) {
        // Non-authenticated users have no access
        setHasAccess(false);
        setPlanName(null);
        setAmount(null);
        setLoading(false);
        return;
      }

      // SECURITY FIX: Use secure RPC function for server-side verification only
      const { data: accessData, error: accessError } = await supabase.rpc(
        'verify_user_access',
        { user_uuid: user.id }
      );

      if (accessError) {
        console.error('Error verifying user access:', accessError);
        setHasAccess(false);
        setPlanName(null);
        setAmount(null);
        setLoading(false);
        return;
      }

      // Type cast the RPC result
      const typedAccessData = accessData as unknown as VerifyUserAccessResult;

      // Set access based on server-side verification only
      if (typedAccessData?.has_access) {
        setHasAccess(true);
        setPlanName(typedAccessData.plan_name);
        setAmount(typedAccessData.amount);
        setDaysRemaining(typedAccessData.days_remaining ?? null);
        setExpiresAt(typedAccessData.expires_at ?? null);

        // Update localStorage for UI optimization only (never for authorization)
        localStorage.setItem('payment_plan', typedAccessData.plan_name || '');
        localStorage.setItem('payment_amount', typedAccessData.amount?.toString() || '0');
      } else {
        // Try to link existing guest payment if session ID exists
        if (storedSessionId) {
          console.log('Attempting to link existing payment to user account');
          
          try {
            const { data: linkResult } = await supabase.functions.invoke('link-payment-to-user', {
              body: { sessionId: storedSessionId },
              headers: {
                Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
              }
            });

            if (linkResult?.success) {
              console.log('Successfully linked payment to user account');
              // Refresh payment status after linking
              setTimeout(() => checkPaymentStatus(), 1000);
              return;
            }
          } catch (linkingError) {
            console.log('Payment linking failed:', linkingError);
          }
        }

        // No access found
        setHasAccess(false);
        setPlanName(null);
        setAmount(null);
        setDaysRemaining(null);
        setExpiresAt(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setHasAccess(false);
      setPlanName(null);
      setAmount(null);
      setLoading(false);
    }
  }, []);

  const markPaymentCompleted = useCallback(() => {
    // SECURITY FIX: Don't set localStorage for authorization, just recheck from server
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Use real-time hook for access changes
  useRealtimeAccess(userEmail, checkPaymentStatus);

  useEffect(() => {
    checkPaymentStatus();
    
    // Listen for storage changes (in case payment session is updated in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'payment_session_id') {
        checkPaymentStatus();
      }
    };

    // Listen for auth state changes to recheck payment status
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          checkPaymentStatus();
        } else if (event === 'SIGNED_OUT') {
          setHasAccess(false);
          setPlanName(null);
          setAmount(null);
          setUserEmail(undefined);
          setLoading(false);
          // Clear localStorage on sign out
          localStorage.removeItem('payment_plan');
          localStorage.removeItem('payment_amount');
        }
      }
    );

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      subscription.unsubscribe();
    };
  }, [checkPaymentStatus]);

  return {
    hasAccess,
    hasPaid: hasAccess,
    planName,
    amount,
    daysRemaining,
    expiresAt,
    loading,
    markPaymentCompleted
  };
};